(() => {
  function initPpbInquiry(section) {
    if (!section || section.dataset.ppbInquiryInitialized === 'true') return;
    section.dataset.ppbInquiryInitialized = 'true';

    const form = section.querySelector('form');
    const questionPanels = Array.from(section.querySelectorAll('[data-ppb-question]'))
      .sort((a, b) => Number(a.dataset.ppbStep) - Number(b.dataset.ppbStep));
    questionPanels.forEach((panel) => form.appendChild(panel));

    const panels = Array.from(section.querySelectorAll('[data-ppb-step]'));
    const summaryBody = section.querySelector('[data-ppb-summary-body]');
    const startButton = section.querySelector('[data-ppb-start]');
    const progress = section.querySelector('[data-ppb-progress]');
    const progressBar = section.querySelector('[data-ppb-progress-bar]');
    let hasStarted = false;

    // =========================
    // Shared Discovery Workflow
    // =========================

    function getInputs(fieldName) {
      return Array.from(section.querySelectorAll(`[data-ppb-field="${fieldName}"]`));
    }

    function getCombinedTimeValue(fieldName) {
      const group = section.querySelector(`[data-ppb-time-group="${fieldName}"]`);
      if (!group || group.querySelector('select:not(:disabled)') === null) return null;
      const hour = group.querySelector('[data-ppb-time-part="hour"]')?.value || '';
      const minute = group.querySelector('[data-ppb-time-part="minute"]')?.value || '';
      const period = group.querySelector('[data-ppb-time-part="period"]')?.value || '';
      return hour && minute && period ? `${hour}:${minute} ${period}` : '';
    }

    function getValue(fieldName) {
      const time = getCombinedTimeValue(fieldName);
      if (time !== null) return time;
      return getInputs(fieldName).reduce((values, input) => {
        if (input.disabled) return values;
        if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return values;
        if (input.value) values.push(input.value);
        return values;
      }, []).join(', ');
    }

    function dateNotFinalized() {
      return getValue('Event Date Type') === "We haven't finalized the date yet";
    }

    function todayValue() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }

    function syncEventType() {
      const customerType = getValue('Customer Type');
      section.querySelectorAll('[data-ppb-event-types]').forEach((group) => {
        const active = group.dataset.ppbEventTypes.split('|').includes(customerType);
        group.classList.toggle('is-visible', active);
        group.querySelectorAll('select').forEach((select) => {
          select.disabled = !active;
          if (!active) select.value = '';
        });
      });
    }

    function syncEventDates() {
      const dateType = getValue('Event Date Type');
      const message = section.querySelector('[data-ppb-date-message]');
      let clearedPastDate = false;
      section.querySelectorAll('[data-ppb-event-date]').forEach((input) => {
        input.min = todayValue();
        const group = input.closest('[data-ppb-date-fields]');
        const active = group.dataset.ppbDateFields === dateType && !dateNotFinalized();
        group.classList.toggle('is-visible', active);
        input.disabled = !active;
        if (!active) input.value = '';
        if (input.value && input.value < todayValue()) {
          input.value = '';
          clearedPastDate = true;
        }
      });
      if (message) message.textContent = clearedPastDate ? "Please select today's date or a future date." : '';

      section.querySelectorAll('[data-ppb-event-time] select').forEach((select) => {
        select.disabled = dateNotFinalized();
        select.required = !dateNotFinalized();
        if (dateNotFinalized()) select.value = '';
      });
    }

    function dateComplete() {
      if (dateNotFinalized()) return true;
      if (getValue('Event Date Type') === 'Single-day event') return Boolean(getValue('Event Date'));
      if (getValue('Event Date Type') === 'Multi-day event') return Boolean(getValue('Event Start Date') && getValue('Event End Date'));
      return false;
    }

    function pipeDrapeSelected() {
      return servicesInclude('Pipe & Drape');
    }

    function balloonStylingSelected() {
      return servicesInclude('Balloon Styling');
    }

    function balloonSomethingElseSelected() {
      return getValue('Balloon Styling Types').split(', ').includes('Something Else');
    }

    function segBackdropSelected() {
      return servicesInclude('SEG Backdrops');
    }

    function syncPlanningWorkflow() {
      section.querySelectorAll('[data-ppb-step="7.3"] [data-ppb-time-part], [data-ppb-step="7.4"] [data-ppb-time-part]').forEach((select) => {
        const timelineActive = !dateNotFinalized();
        select.disabled = !timelineActive;
        select.required = timelineActive;
        if (!timelineActive) select.value = '';
      });

      const status = getValue('Venue Status');
      section.querySelectorAll('[data-ppb-venue-fields], [data-ppb-venue-note]').forEach((group) => {
        const target = group.dataset.ppbVenueFields || group.dataset.ppbVenueNote;
        const show = target === status;
        group.classList.toggle('is-visible', show);
        group.querySelectorAll('input').forEach((input) => {
          input.disabled = !show;
          input.required = show;
          if (!show) input.value = '';
        });
      });

      const role = getValue('Pipe & Drape Role');
      const other = section.querySelector('[data-ppb-pipe-drape-other]');
      if (other) {
        const show = pipeDrapeSelected() && role === 'Other';
        other.classList.toggle('is-visible', show);
        other.querySelectorAll('textarea').forEach((input) => {
          input.disabled = !show;
          input.required = show;
          if (!show) input.value = '';
        });
      }

      const segPanel = section.querySelector('[data-ppb-seg-backdrop]');
      if (segPanel) {
        const show = segBackdropSelected();
        segPanel.querySelectorAll('input').forEach((input) => {
          input.disabled = !show;
          input.required = show;
          if (!show) input.checked = false;
        });
      }

      const balloonPanel = section.querySelector('[data-ppb-balloon-styling]');
      if (balloonPanel) {
        const show = balloonStylingSelected();
        balloonPanel.querySelectorAll('input').forEach((input) => {
          input.disabled = !show;
          if (!show) input.checked = false;
        });
      }

      const balloonOtherPanel = section.querySelector('[data-ppb-balloon-something-else-panel]');
      if (balloonOtherPanel) {
        const show = balloonStylingSelected() && balloonSomethingElseSelected();
        balloonOtherPanel.querySelectorAll('textarea').forEach((input) => {
          input.disabled = !show;
          input.required = show;
          if (!show) input.value = '';
        });
      }
    }

    // =========================
    // Shared Personalization Workflow
    // =========================

    function meaningful(fieldName) {
      return Boolean(getValue(fieldName).trim());
    }

    function servicesInclude(value) {
      return getValue('Services Interested In').split(', ').includes(value);
    }

    function syncConditionalDescriptions() {
      section.querySelectorAll('[data-ppb-something-else-panel]').forEach((panel) => {
        const active = servicesInclude('Something Else');
        panel.querySelectorAll('textarea').forEach((input) => {
          input.disabled = !active;
          input.required = active;
          if (!active) input.value = '';
        });
      });
    }

    function syncInspiration() {
      const active = getValue('Inspiration Preference') === 'Yes';
      const fields = section.querySelector('[data-ppb-inspiration-fields]');
      if (!fields) return;
      fields.classList.toggle('is-visible', active);
      fields.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        input.required = active;
        if (!active) input.value = '';
      });
    }

    function syncReferral() {
      const active = getValue('Referral Source') === 'Other';
      const fields = section.querySelector('[data-ppb-referral-other]');
      if (!fields) return;
      fields.classList.toggle('is-visible', active);
      fields.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        input.required = active;
        if (!active) input.value = '';
      });
    }

    function formatPhone(value) {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    function syncPhone() {
      const phone = getInputs('Phone')[0];
      if (phone) phone.value = formatPhone(phone.value);
    }

    function normalizedNamePart(fieldName) {
      return getValue(fieldName).trim().replace(/\s+/g, ' ');
    }

    function combinedName() {
      return [normalizedNamePart('First Name'), normalizedNamePart('Last Name')].filter(Boolean).join(' ');
    }

    function syncStructuredName() {
      const firstName = normalizedNamePart('First Name');
      const lastName = normalizedNamePart('Last Name');
      const combined = section.querySelector('[data-ppb-combined-name]');
      if (combined) combined.value = [firstName, lastName].filter(Boolean).join(' ');
    }

    // =========================
    // Validation and Submission
    // =========================

    function isPrimarilyEnglish(value) {
      const text = value.trim().toLowerCase();
      if (!text) return true;
      const words = text.match(/[a-zÀ-ÿ']+/g) || [];
      if (words.length <= 3) return true;
      const englishWords = new Set(['a','about','after','all','also','an','and','area','as','at','backdrop','backdrops','balloon','birthday','black','brand','branded','celebration','ceremony','columns','corporate','custom','decor','display','event','for','from','gold','have','help','in','is','it','like','looking','love','modern','need','of','on','or','our','party','photo','planning','please','room','setup','share','stage','style','the','their','this','to','use','we','welcome','with','would','you']);
      const nonEnglishWords = new Set(['con','de','del','des','el','en','et','la','las','le','les','los','para','por','que','un','una','une','y']);
      const englishCount = words.filter((word) => englishWords.has(word.replace(/^'+|'+$/g, ''))).length;
      const nonEnglishCount = words.filter((word) => nonEnglishWords.has(word.replace(/^'+|'+$/g, ''))).length;
      const letters = text.match(/\p{L}/gu) || [];
      const ascii = text.match(/[a-z]/g) || [];
      if (letters.length && ascii.length / letters.length < 0.7) return false;
      if (nonEnglishCount >= 2 && nonEnglishCount > englishCount) return false;
      return !(words.length >= 6 && englishCount === 0);
    }

    function englishValid(fieldName) {
      return isPrimarilyEnglish(getValue(fieldName));
    }

    function shouldIncludePanel(panel) {
      if (panel.hasAttribute('data-ppb-balloon-styling')) return balloonStylingSelected();
      if (panel.hasAttribute('data-ppb-balloon-something-else-panel')) return balloonStylingSelected() && balloonSomethingElseSelected();
      if (panel.hasAttribute('data-ppb-something-else-panel')) return servicesInclude('Something Else');
      if (panel.hasAttribute('data-ppb-seg-backdrop')) return segBackdropSelected();
      if (panel.hasAttribute('data-ppb-pipe-drape')) return pipeDrapeSelected();
      if (panel.hasAttribute('data-ppb-event-time')) return !dateNotFinalized();
      if (['7.3', '7.4'].includes(panel.dataset.ppbStep)) return !dateNotFinalized();
      return true;
    }

    function panelComplete(panel) {
      const step = panel.dataset.ppbStep;
      const rules = {
        '1': () => meaningful('Customer Type'),
        '2': () => meaningful('Event Type'),
        '3': dateComplete,
        '3.5': () => meaningful('Event Start Time'),
        '3.55': () => meaningful('Event End Time'),
        '5': () => meaningful('Services Interested In'),
        '5.02': () => meaningful('Balloon Styling Types'),
        '5.025': () => meaningful('Balloon Styling — Something Else') && englishValid('Balloon Styling — Something Else'),
        '5.05': () => meaningful('SEG Backdrop Type'),
        '5.1': () => meaningful('Something Else Requested') && englishValid('Something Else Requested'),
        '5.2': () => meaningful('Pipe & Drape Role') && (getValue('Pipe & Drape Role') !== 'Other' || (meaningful('Pipe & Drape Other Description') && englishValid('Pipe & Drape Other Description'))),
        '6': () => meaningful('Venue Status'),
        '7': () => getValue('Venue Status') === 'Yes' ? meaningful('Venue Name') && meaningful('Venue City/State') : meaningful('General Location'),
        '7.2': () => meaningful('Venue Type'),
        '7.3': () => meaningful('Setup Begin Time'),
        '7.4': () => meaningful('Breakdown Begin Time'),
        '7.5': () => meaningful('Budget Range'),
        '9': () => meaningful('Inspiration Preference') && (getValue('Inspiration Preference') !== 'Yes' || inspirationValid()),
        '9.1': () => meaningful('Vision Notes') && englishValid('Vision Notes'),
        '14': () => meaningful('First Name') && meaningful('Last Name') && meaningful('Email') && referralValid() && sharedEnglishValid()
      };
      return rules[step] ? Boolean(rules[step]()) : true;
    }

    function inspirationValid() {
      const input = getInputs('Inspiration Link')[0];
      return Boolean(input && input.value.trim() && input.checkValidity());
    }

    function referralValid() {
      if (!meaningful('Referral Source')) return false;
      return getValue('Referral Source') !== 'Other' || (meaningful('Referral Source Other') && englishValid('Referral Source Other'));
    }

    function sharedEnglishValid() {
      return ['Vision Notes', 'Something Else Requested', 'Balloon Styling — Something Else', 'Pipe & Drape Other Description', 'Referral Source Other'].every(englishValid);
    }

    function handleExclusiveService(input) {
      if (!input.matches('[data-ppb-field="Services Interested In"]') || !input.checked) return;
      const group = Array.from(section.querySelectorAll(`input[name="${input.name}"]`));
      const notSure = group.find((item) => item.hasAttribute('data-ppb-not-sure-service'));
      if (!notSure) return;
      if (input === notSure) group.forEach((item) => { if (item !== input) item.checked = false; });
      else notSure.checked = false;
    }

    function firstControl(panel) {
      const step = panel.dataset.ppbStep;
      const fieldByStep = {
        '1':'Customer Type','2':'Event Type','3':'Event Date Type','3.5':'Event Start Time','3.55':'Event End Time','5':'Services Interested In','5.02':'Balloon Styling Types','5.025':'Balloon Styling — Something Else','5.05':'SEG Backdrop Type','5.1':'Something Else Requested','5.2':'Pipe & Drape Role','6':'Venue Status','7':'Venue Name','7.2':'Venue Type','7.3':'Setup Begin Time','7.4':'Breakdown Begin Time','7.5':'Budget Range','9':'Inspiration Preference','9.1':'Vision Notes','14':'First Name'
      };
      const fieldName = fieldByStep[step];
      if (step === '5.2' && getValue('Pipe & Drape Role') === 'Other') return getInputs('Pipe & Drape Other Description').find((input) => !input.disabled);
      if (step === '7') {
        const venueFields = getValue('Venue Status') === 'Yes' ? ['Venue Name', 'Venue City/State'] : ['General Location'];
        return venueFields.flatMap(getInputs).find((input) => !input.disabled && !input.value.trim());
      }
      if (step === '9' && getValue('Inspiration Preference') === 'Yes') return getInputs('Inspiration Link').find((input) => !input.disabled);
      if (step === '14') {
        const names = ['First Name', 'Last Name', 'Email', 'Referral Source'];
        if (getValue('Referral Source') === 'Other') names.push('Referral Source Other');
        return names.flatMap(getInputs).find((input) => !input.disabled && (!input.value.trim() || !input.checkValidity()));
      }
      const timeGroup = fieldName && section.querySelector(`[data-ppb-time-group="${fieldName}"]`);
      if (timeGroup) return Array.from(timeGroup.querySelectorAll('select:not(:disabled)')).find((select) => !select.value) || timeGroup.querySelector('select:not(:disabled)');
      return (fieldName && getInputs(fieldName).find((input) => !input.disabled && (!input.value.trim() || (input.matches(':checked') === false && ['radio', 'checkbox'].includes(input.type))))) || panel.querySelector('input:not(:disabled), select:not(:disabled), textarea:not(:disabled)');
    }

    function showInvalid(field) {
      if (!field) return;
      const fieldName = field.dataset.ppbField || field.closest('[data-ppb-time-group]')?.dataset.ppbTimeGroup;
      const message = fieldName && section.querySelector(`[data-ppb-validation-for="${fieldName}"]`);
      if (message) message.hidden = false;
      field.closest('[data-ppb-question]')?.classList.add('is-visible');
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      field.focus({ preventScroll: true });
    }

    function revealPanels() {
      let canReveal = hasStarted;
      questionPanels.forEach((panel) => {
        const show = canReveal && shouldIncludePanel(panel);
        panel.classList.toggle('is-visible', show);
        panel.querySelectorAll('input, select, textarea').forEach((control) => {
          if (!show) control.setAttribute('aria-hidden', 'true');
          else control.removeAttribute('aria-hidden');
        });
        if (show && !panelComplete(panel)) canReveal = false;
      });
    }

    function syncProgress() {
      if (!progress || !progressBar) return;
      progress.classList.toggle('is-visible', hasStarted);
      const included = questionPanels.filter(shouldIncludePanel);
      const complete = included.filter(panelComplete).length;
      progressBar.style.width = `${hasStarted && included.length ? Math.max(4, Math.round(complete / included.length * 100)) : 0}%`;
    }

    function selectedList(fieldName) {
      return getValue(fieldName).split(', ').map((item) => item.trim()).filter(Boolean);
    }

    function formatDate(value) {
      if (!value) return '';
      const parts = value.split('-').map(Number);
      if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) return value;
      const date = new Date(parts[0], parts[1] - 1, parts[2]);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    function formattedEventDate() {
      if (dateNotFinalized()) return 'Not yet finalized';
      if (getValue('Event Date Type') === 'Single-day event') return formatDate(getValue('Event Date'));
      if (getValue('Event Date Type') === 'Multi-day event') {
        const start = formatDate(getValue('Event Start Date'));
        const end = formatDate(getValue('Event End Date'));
        return [start, end].filter(Boolean).join('–');
      }
      return '';
    }

    function parseTimeMinutes(value) {
      const match = value.match(/^(\d{1,2}):(\d{2})\s(AM|PM)$/);
      if (!match) return null;
      let hour = Number(match[1]);
      const minute = Number(match[2]);
      const period = match[3];
      if (hour === 12) hour = 0;
      if (period === 'PM') hour += 12;
      return hour * 60 + minute;
    }

    function availableSetupWindow() {
      const setup = parseTimeMinutes(getValue('Setup Begin Time'));
      const start = parseTimeMinutes(getValue('Event Start Time'));
      if (setup === null || start === null || start < setup) return '';
      const diff = start - setup;
      const hours = Math.floor(diff / 60);
      const minutes = diff % 60;
      const parts = [];
      if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
      if (minutes) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
      return parts.join(' ') || '0 minutes';
    }

    function addLine(lines, label, value) {
      if (value) lines.push(`${label}: ${value}`);
    }

    function addList(lines, label, values) {
      if (!values.length) return;
      lines.push(`${label}:`);
      values.forEach((value) => lines.push(`- ${value}`));
    }

    function pushSection(sections, title, lines) {
      if (lines.length) sections.push([title, ...lines].join('\n'));
    }

    function syncHiddenSummary() {
      if (!summaryBody) return;

      const services = selectedList('Services Interested In');
      const balloonTypes = balloonStylingSelected() ? selectedList('Balloon Styling Types') : [];
      const setupWindow = availableSetupWindow();
      const venueName = getValue('Venue Status') === 'Yes' ? getValue('Venue Name') : '';
      const location = getValue('Venue Status') === 'Yes' ? getValue('Venue City/State') : getValue('General Location');
      const sections = ['NEW PRETTY PRETTY BACKDROPS INQUIRY'];

      const glance = [];
      addLine(glance, 'Event', getValue('Event Type'));
      addLine(glance, 'Customer Type', getValue('Customer Type'));
      addLine(glance, 'Date', formattedEventDate());
      addLine(glance, 'Budget', getValue('Budget Range'));
      addLine(glance, 'Venue', venueName);
      addLine(glance, 'Location', location);
      addList(glance, 'Services', services);
      addLine(glance, 'Available Setup Window', setupWindow);
      pushSection(sections, 'AT A GLANCE', glance);

      const client = [];
      addLine(client, 'Name', combinedName());
      addLine(client, 'Email', getValue('Email'));
      addLine(client, 'Mobile Phone', getValue('Phone'));
      addLine(client, 'Preferred Contact Method', getValue('Preferred Contact Method'));
      pushSection(sections, 'CLIENT', client);

      const eventDetails = [];
      addLine(eventDetails, 'Customer Type', getValue('Customer Type'));
      addLine(eventDetails, 'Event Type', getValue('Event Type'));
      addLine(eventDetails, dateNotFinalized() || getValue('Event Date Type') === 'Single-day event' ? 'Event Date' : 'Event Dates', formattedEventDate());
      if (!dateNotFinalized()) addLine(eventDetails, 'Event Time', [getValue('Event Start Time'), getValue('Event End Time')].filter(Boolean).join('–'));
      addLine(eventDetails, 'Decor Budget', getValue('Budget Range'));
      pushSection(sections, 'EVENT DETAILS', eventDetails);

      const venue = [];
      addLine(venue, 'Venue', venueName);
      addLine(venue, 'Venue Type', getValue('Venue Type'));
      addLine(venue, 'Location', location);
      addLine(venue, 'Setup Can Begin', getValue('Setup Begin Time'));
      addLine(venue, 'Event Begins', getValue('Event Start Time'));
      addLine(venue, 'Available Setup Window', setupWindow);
      addLine(venue, 'Event Ends', getValue('Event End Time'));
      addLine(venue, 'Breakdown Can Begin', getValue('Breakdown Begin Time'));
      pushSection(sections, 'VENUE & LOGISTICS', venue);

      const serviceLines = [];
      services.forEach((service) => serviceLines.push(`- ${service}`));
      if (balloonStylingSelected()) {
        addList(serviceLines, 'Balloon Styling Types', balloonTypes);
        if (balloonSomethingElseSelected()) addLine(serviceLines, 'Balloon Styling — Something Else', getValue('Balloon Styling — Something Else'));
      }
      if (pipeDrapeSelected()) {
        addLine(serviceLines, 'Pipe & Drape Solution', getValue('Pipe & Drape Role'));
        if (getValue('Pipe & Drape Role') === 'Other') addLine(serviceLines, 'Pipe & Drape Details', getValue('Pipe & Drape Other Description'));
      }
      if (segBackdropSelected()) addLine(serviceLines, 'SEG Backdrop Type', getValue('SEG Backdrop Type'));
      if (servicesInclude('Something Else')) addLine(serviceLines, 'Something Else Requested', getValue('Something Else Requested'));
      pushSection(sections, 'SERVICES REQUESTED', serviceLines);

      const vision = [];
      if (getValue('Vision Notes')) vision.push(`Vision Notes:\n${getValue('Vision Notes')}`);
      if (getValue('Inspiration Preference') === 'Yes') addLine(vision, 'Inspiration Link', getValue('Inspiration Link'));
      if (getValue('Inspiration Preference') === 'Not today') addLine(vision, 'Inspiration', 'Not provided');
      pushSection(sections, 'VISION & INSPIRATION', vision);

      const referral = [];
      addLine(referral, 'How They Heard About PPB', getValue('Referral Source'));
      if (getValue('Referral Source') === 'Other') addLine(referral, 'Details', getValue('Referral Source Other'));
      pushSection(sections, 'REFERRAL', referral);

      summaryBody.value = sections.join('\n\n');
    }

    function sync() {
      syncEventType();
      syncEventDates();
      syncPlanningWorkflow();
      syncConditionalDescriptions();
      syncInspiration();
      syncReferral();
      syncStructuredName();
      syncHiddenSummary(false);
      revealPanels();
      syncProgress();
    }

    startButton?.addEventListener('click', () => {
      hasStarted = true;
      sync();
      const first = panels.find((panel) => panel.dataset.ppbStep === '1');
      first?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      first?.querySelector('input, select, textarea')?.focus({ preventScroll: true });
    });

    section.addEventListener('change', (event) => {
      handleExclusiveService(event.target);
      sync();
    });
    section.addEventListener('input', (event) => {
      if (event.target.matches('[data-ppb-field="Phone"]')) syncPhone();
      const message = event.target.dataset.ppbField && section.querySelector(`[data-ppb-validation-for="${event.target.dataset.ppbField}"]`);
      if (message && event.target.value.trim()) message.hidden = true;
      sync();
    });

    form?.addEventListener('invalid', (event) => {
      if (!event.target.matches('[data-ppb-field], [data-ppb-time-part]')) return;
      event.preventDefault();
      showInvalid(event.target);
    }, true);

    form?.addEventListener('submit', (event) => {
      sync();
      const incomplete = questionPanels.filter(shouldIncludePanel).find((panel) => !panelComplete(panel));
      if (incomplete) {
        event.preventDefault();
        event.stopPropagation();
        showInvalid(firstControl(incomplete));
        return;
      }
      syncStructuredName();
      syncHiddenSummary(true);
    });

    syncPhone();
    sync();
  }

  function initAllPpbInquiries() {
    document.querySelectorAll('.ppb-inquiry').forEach(initPpbInquiry);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAllPpbInquiries);
  else initAllPpbInquiries();

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('.ppb-inquiry').forEach(initPpbInquiry);
  });
})();